import InfiniteScroll from "react-infinite-scroll-component";
import { downloadCertificate } from "@/api/williams/certificates";
import { ExpandableCard, PinButton, SearchField } from "../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getLinkPath } from "./lookup/links";
import { GlobalSearchResult } from "@/api/williams/search";
import { highlight } from "@/util";
import { Link, useSearchParams } from "react-router-dom";
import { String } from "@/util";
import { useGlobalSearch } from "./hooks/useGlobalSearch";
import { usePinResult } from "./hooks/usePinResult";
import { useState } from "react";

export function GlobalSearch() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("search") ?? undefined;
  const search = useGlobalSearch({ searchString: query });

  const data = search.data?.pages.flatMap((page) => page.results) ?? [];
  const total = search.data?.pages[0]?.total ?? 0;

  return (
    <>
      <h3 className="mb-2">Search</h3>
      <div className="d-block d-md-none mb-3">
        <SearchField />
      </div>
      <ExpandableCard caption={`Search Results (${total})`} expanded={true} allowExpand={false}>
        <InfiniteScroll
          dataLength={data.length}
          next={search.fetchNextPage}
          hasMore={Boolean(search.hasNextPage)}
          loader={<p>Loading...</p>}
        >
          {search.isLoading ? (
            <p>Loading...</p>
          ) : search.isError ? (
            <p>An unknown error occurred.</p>
          ) : search.data && data.length > 0 ? (
            <SearchResultsTable data={data} search={query} />
          ) : query && query.length > 0 ? (
            <p>Your search returned no results.</p>
          ) : (
            <p>Please search for something.</p>
          )}
        </InfiniteScroll>
      </ExpandableCard>
    </>
  );
}

function SearchResultsTable({ data = [], search }: { data: GlobalSearchResult[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-4">Name</th>
          <th className="col-4 d-none d-md-table-cell">Type</th>
          <th className="col-4 d-none d-md-table-cell">Description</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((result) => (
          <SearchResult key={result.id} result={result} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function SearchResult({ result, search }: { result: GlobalSearchResult; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const pin = usePinResult();

  return (
    <>
      <tr key={`${result.id}_${result.pinned}`}>
        <td>
          {result.resultType === "Certificate" || result.resultType === "Test" ? (
            <a className="link-primary" onClick={() => downloadCertificate(result.id)}>
              {highlight(result.name, search)}
            </a>
          ) : (
            <Link to={getLinkPath(result)} className="link link-primary">
              {highlight(result.name, search)}
            </Link>
          )}
        </td>
        <td className="d-none d-md-table-cell">{result.resultType}</td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(result.description, 50), search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1 d-inline d-md-none"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={result.pinned} onChange={() => pin.mutate(result)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={3}>
            <ExpandedDetails row={result} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails row={result} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ row, search }: { row: GlobalSearchResult; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Search Result</div>
      <div className="p-1">
        <dl>
          <dt>Type</dt>
          <dd>{row.resultType}</dd>
          {row.description && (
            <>
              <dt>Description</dt>
              <dd>{highlight(row.description, search)}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
